import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import Logo from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña actualizada. Inicia sesión con la nueva contraseña.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#16181D] px-4">
      <div className="w-full max-w-sm rounded-2xl border border-[#2D323E] bg-[#1E2128] p-8">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {!token ? (
          <p data-testid="reset-password-invalid" className="text-center text-sm text-[#EF4444]">
            El enlace no es válido. Solicita uno nuevo desde «¿Has olvidado la contraseña?».
          </p>
        ) : (
          <>
            <h1 className="mb-1 text-center font-heading text-xl font-bold">Nueva contraseña</h1>
            <p className="mb-7 text-center text-sm text-[#8A8F98]">Elige una contraseña segura</p>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="rp-password" className="text-[#8A8F98]">Nueva contraseña</Label>
                <Input
                  id="rp-password"
                  data-testid="reset-password-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 border-[#2D323E] bg-[#16181D] text-[#F2F4F6]"
                />
              </div>
              <div>
                <Label htmlFor="rp-confirm" className="text-[#8A8F98]">Repite la contraseña</Label>
                <Input
                  id="rp-confirm"
                  data-testid="reset-password-confirm-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 border-[#2D323E] bg-[#16181D] text-[#F2F4F6]"
                />
              </div>
              {error && (
                <p data-testid="reset-password-error" className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2 text-sm text-[#EF4444]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="reset-password-submit-btn"
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] py-3 font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {loading ? "Guardando…" : "Cambiar contraseña"}
              </button>
            </form>
          </>
        )}
        <div className="mt-6 text-center">
          <Link to="/admin/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1.5 text-sm text-[#8A8F98] transition-colors hover:text-[#F2F4F6]">
            <ArrowLeft className="h-4 w-4" /> Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
